"use client";

import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

export interface SidebarSection {
  id: string;
  label: string;
}

interface ProjectSidebarProps {
  sections: SidebarSection[];
}

const NAV_OFFSET = 80; // fixed nav height + breathing room

const ProjectSidebar: React.FC<ProjectSidebarProps> = ({ sections }) => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const rafRef = useRef<number>(0);

  const handleClick = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const update = () => {
      const scrollY = window.scrollY;
      setIsVisible(scrollY > window.innerHeight * 0.6);

      let current = "";
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= NAV_OFFSET + 40) {
            current = section.id;
          }
        }
      }
      setActiveSection(current);
      rafRef.current = 0;
    };

    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [sections]);

  useEffect(() => { setMounted(true); }, []);

  if (!mounted) return null;

  // The rail blends by difference so it inverts over dark sections (e.g. the
  // Animalia component showcase turning black). Colours are the complements of
  // the intended ones on the near-white page: #eee → #111, #3f3f3f → #c0c0c0,
  // #777 → #888, #909090 → #6f6f6f.
  return createPortal(
    <nav
      className={clsx(
        "fixed left-6 lg:left-10 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-4 mix-blend-difference transition-[opacity,transform] duration-500",
        isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4 pointer-events-none"
      )}
      aria-label="Page sections"
    >
      {sections.map((section) => (
        <button
          key={section.id}
          onClick={() => handleClick(section.id)}
          // Only the indicator is hoverable. Hidden labels sit outside the
          // button's box so a cursor resting over page content can't reveal them.
          className="group relative flex h-4 w-10 items-center text-left"
          aria-current={activeSection === section.id ? "location" : undefined}
        >
          {/* Indicator line */}
          <span
            className={clsx(
              "block h-px transition-[width,background-color] duration-300",
              activeSection === section.id
                ? "w-8 bg-[#eeeeee]"
                : "w-4 bg-[#3f3f3f] group-hover:w-6 group-hover:bg-[#777777] group-focus-visible:w-6 group-focus-visible:bg-[#777777]"
            )}
          />
          {/* Label */}
          <span
            className={clsx(
              "pointer-events-none absolute left-full top-1/2 ml-1 -translate-y-1/2 text-[11px] uppercase tracking-[0.15em] font-pp-neuebit transition-[color,opacity] duration-300 whitespace-nowrap",
              activeSection === section.id
                ? "text-[#eeeeee] opacity-100"
                : "text-[#909090] opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
            )}
          >
            {section.label}
          </span>
        </button>
      ))}
    </nav>,
    document.body
  );
};

export default ProjectSidebar;
